import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  Timestamp,
  writeBatch,
  limit
} from 'firebase/firestore';
import { db } from '../lib/firebase';

// Types for store operations
export interface Product {
  id: string;
  // Legacy fields (for backward compatibility)
  name?: string;
  description?: string;
  category?: string;
  // Multilingual fields
  name_pt: string;
  name_en: string;
  name_fr: string;
  description_pt?: string;
  description_en?: string;
  description_fr?: string;
  category_pt: string;
  category_en: string;
  category_fr: string;
  // Common fields
  price: number;
  image: string;
  images: string[];
  quantity: number;
  enabled: boolean;
  position: number;
  created_at?: Date | Timestamp;
  updated_at?: Date | Timestamp;
}

export interface Order {
  id?: string;
  order_number: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  customer_address: string;
  customer_city: string;
  customer_postal_code: string;
  customer_country: string;
  pickup_location: string;
  pickup_date?: string;
  pickup_time?: string;
  total_amount: number;
  status: 'pending' | 'confirmed' | 'shipped' | 'delivered' | 'cancelled';
  items: OrderItem[];
  created_at?: Date | Timestamp;
  updated_at?: Date | Timestamp;
}

export interface OrderItem {
  id?: string;
  order_id?: string;
  product_id: string;
  product_name: string;
  product_price: number;
  quantity: number;
  subtotal: number;
}

export interface CartItem {
  product: Product;
  quantity: number;
}

// Helper function to convert Firestore timestamp to Date
const convertTimestamp = (timestamp: any): Date | undefined => {
  if (!timestamp) return undefined;
  if (timestamp instanceof Date) return timestamp;
  if (timestamp.toDate) return timestamp.toDate();
  return new Date(timestamp);
};

// Helper function to convert Product from Firestore
const convertProduct = (doc: any): Product => {
  const data = doc.data();
  return {
    id: doc.id,
    ...data,
    created_at: convertTimestamp(data.created_at),
    updated_at: convertTimestamp(data.updated_at),
    price: data.price || 0,
    quantity: data.quantity || 0,
    enabled: data.enabled !== false,
    position: data.position || 0,
    images: data.images || []
  };
};

// Helper function to convert Order from Firestore
const convertOrder = (doc: any, items: OrderItem[] = []): Order => {
  const data = doc.data();
  return {
    id: doc.id,
    ...data,
    items,
    created_at: convertTimestamp(data.created_at),
    updated_at: convertTimestamp(data.updated_at)
  };
};

export const storeService = {
  // Product operations
  getProducts: async (): Promise<Product[]> => {
    try {
      const productsRef = collection(db, 'products');
      const q = query(
        productsRef,
        where('enabled', '==', true),
        orderBy('position', 'asc'),
        orderBy('created_at', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => convertProduct(doc));
    } catch (error) {
      console.error('Error fetching products:', error);
      throw new Error(`Failed to fetch products: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  getProductById: async (id: string): Promise<Product | null> => {
    try {
      const productRef = doc(db, 'products', id);
      const productSnap = await getDoc(productRef);
      
      if (!productSnap.exists()) {
        return null;
      }
      
      const product = convertProduct(productSnap);
      
      // Check if product is enabled
      if (!product.enabled) {
        return null;
      }
      
      return product;
    } catch (error) {
      console.error('Error fetching product:', error);
      throw new Error(`Failed to fetch product: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  getProductsByCategory: async (category: string): Promise<Product[]> => {
    try {
      const productsRef = collection(db, 'products');
      
      // Firestore doesn't support OR queries easily, so we'll fetch all enabled products and filter
      const q = query(
        productsRef,
        where('enabled', '==', true),
        orderBy('position', 'asc'),
        orderBy('created_at', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const products = querySnapshot.docs
        .map(doc => convertProduct(doc))
        .filter(product => 
          product.category_pt === category || 
          product.category_en === category || 
          product.category_fr === category
        );
      
      return products;
    } catch (error) {
      console.error('Error fetching products by category:', error);
      return [];
    }
  },

  // Admin function to get product by ID (including disabled ones)
  getProductByIdAdmin: async (id: string): Promise<Product | null> => {
    try {
      const productRef = doc(db, 'products', id);
      const productSnap = await getDoc(productRef);
      
      if (!productSnap.exists()) {
        return null;
      }
      
      return convertProduct(productSnap);
    } catch (error) {
      console.error('Error fetching product (admin):', error);
      throw new Error(`Failed to fetch product: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Admin function to get all products (including disabled ones)
  getAllProducts: async (): Promise<Product[]> => {
    try {
      const productsRef = collection(db, 'products');
      const q = query(
        productsRef,
        orderBy('position', 'asc'),
        orderBy('created_at', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => convertProduct(doc));
    } catch (error) {
      console.error('Error fetching all products:', error);
      throw new Error(`Failed to fetch all products: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Order operations
  createOrder: async (orderData: Omit<Order, 'id' | 'created_at' | 'updated_at'>): Promise<Order> => {
    // Validate stock availability before creating the order
    const stockValidation = await storeService.validateStockAvailability(orderData.items);
    if (!stockValidation.isValid) {
      throw new Error(`Stock validation failed: ${stockValidation.errors.join(', ')}`);
    }

    try {
      const batch = writeBatch(db);
      const now = Timestamp.now();

      // Create the order
      const ordersRef = collection(db, 'orders');
      const orderDocRef = doc(ordersRef);
      
      const orderDataToSave = {
        order_number: orderData.order_number,
        customer_name: orderData.customer_name,
        customer_email: orderData.customer_email,
        customer_phone: orderData.customer_phone,
        customer_address: orderData.customer_address,
        customer_city: orderData.customer_city,
        customer_postal_code: orderData.customer_postal_code,
        customer_country: orderData.customer_country,
        pickup_location: orderData.pickup_location,
        pickup_date: orderData.pickup_date,
        pickup_time: orderData.pickup_time,
        total_amount: orderData.total_amount,
        status: orderData.status,
        created_at: now,
        updated_at: now
      };

      batch.set(orderDocRef, orderDataToSave);

      // Create order items
      const orderItemsRef = collection(db, 'order_items');
      orderData.items.forEach(item => {
        const itemDocRef = doc(orderItemsRef);
        batch.set(itemDocRef, {
          order_id: orderDocRef.id,
          product_id: item.product_id,
          product_name: item.product_name,
          product_price: item.product_price,
          quantity: item.quantity,
          subtotal: item.subtotal,
          created_at: now
        });
      });

      await batch.commit();

      // Fetch the created order with items
      const orderSnap = await getDoc(orderDocRef);
      const itemsSnapshot = await getDocs(
        query(collection(db, 'order_items'), where('order_id', '==', orderDocRef.id))
      );
      
      const items = itemsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as OrderItem));

      const order = convertOrder(orderSnap, items);

      // Send initial order notification emails (to customer and admin)
      try {
        const emailData = {
          orderNumber: order.order_number,
          customerName: order.customer_name,
          customerEmail: order.customer_email,
          status: 'pending',
          totalAmount: order.total_amount,
          items: orderData.items.map(item => ({
            name: item.product_name,
            quantity: item.quantity,
            price: item.product_price
          })),
          pickupLocation: order.pickup_location,
          pickupDate: order.pickup_date,
          pickupTime: order.pickup_time
        };

        // Send emails to both customer and admin for new order
        const emailPromises = [
          storeService.sendNewOrderEmail(emailData),
          storeService.sendOrderAdminNotificationEmail(emailData)
        ];

        await Promise.all(emailPromises);
      } catch (emailError) {
        console.error('Error sending new order notification emails:', emailError);
        // Don't throw error to avoid breaking the order creation process
        // Email failures should not prevent order creation
      }

      return order;
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    }
  },

  getOrderByNumber: async (orderNumber: string): Promise<Order | null> => {
    try {
      const ordersRef = collection(db, 'orders');
      const q = query(ordersRef, where('order_number', '==', orderNumber), limit(1));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return null;
      }

      const orderDoc = querySnapshot.docs[0];
      
      // Fetch order items
      const itemsSnapshot = await getDocs(
        query(collection(db, 'order_items'), where('order_id', '==', orderDoc.id))
      );
      
      const items = itemsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as OrderItem));

      return convertOrder(orderDoc, items);
    } catch (error) {
      console.error('Error fetching order:', error);
      return null;
    }
  },

  getOrderById: async (orderId: string): Promise<Order | null> => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      const orderSnap = await getDoc(orderRef);

      if (!orderSnap.exists()) {
        return null;
      }

      // Fetch order items
      const itemsSnapshot = await getDocs(
        query(collection(db, 'order_items'), where('order_id', '==', orderId))
      );
      
      const items = itemsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as OrderItem));

      return convertOrder(orderSnap, items);
    } catch (error) {
      console.error('Error fetching order by ID:', error);
      return null;
    }
  },

  getAllOrders: async (): Promise<Order[]> => {
    try {
      const ordersRef = collection(db, 'orders');
      const q = query(ordersRef, orderBy('created_at', 'desc'));
      const querySnapshot = await getDocs(q);

      // Fetch items for each order
      const ordersWithItems = await Promise.all(
        querySnapshot.docs.map(async (orderDoc) => {
          const itemsSnapshot = await getDocs(
            query(collection(db, 'order_items'), where('order_id', '==', orderDoc.id))
          );
          
          const items = itemsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          } as OrderItem));

          return convertOrder(orderDoc, items);
        })
      );

      return ordersWithItems;
    } catch (error) {
      console.error('Error fetching orders:', error);
      return [];
    }
  },

  updateOrderStatus: async (orderId: string, status: Order['status']): Promise<Order> => {
    // First, get the current order to check previous status
    const currentOrder = await storeService.getOrderById(orderId);
    if (!currentOrder) {
      throw new Error('Order not found');
    }

    const previousStatus = currentOrder.status;

    try {
      // Update the order status
      const orderRef = doc(db, 'orders', orderId);
      await updateDoc(orderRef, {
        status,
        updated_at: Timestamp.now()
      });

      // Handle stock changes based on status transitions
      try {
        await storeService.handleStockChanges(orderId, previousStatus, status);
      } catch (stockError) {
        console.error('Error handling stock changes:', stockError);
        // Revert the status change if stock update fails
        await updateDoc(orderRef, { status: previousStatus });
        const errorMessage = stockError instanceof Error ? stockError.message : 'Unknown error';
        throw new Error(`Failed to update stock: ${errorMessage}`);
      }

      // Fetch order items
      const itemsSnapshot = await getDocs(
        query(collection(db, 'order_items'), where('order_id', '==', orderId))
      );
      
      const items = itemsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as OrderItem));

      const orderSnap = await getDoc(orderRef);
      return convertOrder(orderSnap, items);
    } catch (error) {
      console.error('Error updating order status:', error);
      throw error;
    }
  },

  // Product management (admin functions)
  createProduct: async (productData: Omit<Product, 'id' | 'created_at' | 'updated_at'>): Promise<Product> => {
    try {
      const now = Timestamp.now();
      const productsRef = collection(db, 'products');
      
      const productDataToSave = {
        ...productData,
        enabled: productData.enabled !== undefined ? productData.enabled : true,
        created_at: now,
        updated_at: now
      };

      const docRef = await addDoc(productsRef, productDataToSave);
      const productSnap = await getDoc(docRef);
      
      return convertProduct(productSnap);
    } catch (error) {
      console.error('Error creating product:', error);
      throw error;
    }
  },

  updateProduct: async (id: string, updates: Partial<Product>): Promise<Product> => {
    try {
      const productRef = doc(db, 'products', id);
      await updateDoc(productRef, {
        ...updates,
        updated_at: Timestamp.now()
      });

      const productSnap = await getDoc(productRef);
      return convertProduct(productSnap);
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  },

  deleteProduct: async (id: string): Promise<void> => {
    try {
      const productRef = doc(db, 'products', id);
      await deleteDoc(productRef);
    } catch (error) {
      console.error('Error deleting product:', error);
      throw error;
    }
  },

  toggleProductEnabled: async (id: string): Promise<Product> => {
    try {
      // First get the current product to check its enabled status
      const productRef = doc(db, 'products', id);
      const productSnap = await getDoc(productRef);

      if (!productSnap.exists()) {
        throw new Error('Product not found');
      }

      const currentProduct = convertProduct(productSnap);
      
      // Toggle the enabled status
      const newEnabledStatus = !currentProduct.enabled;

      await updateDoc(productRef, {
        enabled: newEnabledStatus,
        updated_at: Timestamp.now()
      });

      const updatedProductSnap = await getDoc(productRef);
      return convertProduct(updatedProductSnap);
    } catch (error) {
      console.error('Error toggling product enabled status:', error);
      throw error;
    }
  },

  // Stock management functions
  handleStockChanges: async (orderId: string, previousStatus: Order['status'], newStatus: Order['status']): Promise<void> => {
    // Get order items
    const itemsSnapshot = await getDocs(
      query(collection(db, 'order_items'), where('order_id', '==', orderId))
    );

    if (itemsSnapshot.empty) {
      return; // No items to process
    }

    const items = itemsSnapshot.docs.map(doc => doc.data() as OrderItem);

    // Determine stock changes based on status transitions
    const shouldReduceStock = (prev: Order['status'], curr: Order['status']) => {
      // Reduce stock when moving to 'confirmed' from any other status
      return curr === 'confirmed' && prev !== 'confirmed';
    };

    const shouldRestoreStock = (prev: Order['status'], curr: Order['status']) => {
      // Restore stock when moving to 'cancelled' or 'pending' from 'confirmed'
      return (curr === 'cancelled' || curr === 'pending') && prev === 'confirmed';
    };

    // Process each item
    for (const item of items) {
      if (shouldReduceStock(previousStatus, newStatus)) {
        await storeService.reduceProductStock(item.product_id, item.quantity);
      } else if (shouldRestoreStock(previousStatus, newStatus)) {
        await storeService.restoreProductStock(item.product_id, item.quantity);
      }
    }

    // Send email notifications for status changes
    await storeService.sendOrderStatusEmail(orderId, previousStatus, newStatus);
  },

  reduceProductStock: async (productId: string, quantity: number): Promise<void> => {
    const productRef = doc(db, 'products', productId);
    const productSnap = await getDoc(productRef);

    if (!productSnap.exists()) {
      throw new Error('Product not found');
    }

    const product = convertProduct(productSnap);
    const newQuantity = product.quantity - quantity;
    
    if (newQuantity < 0) {
      throw new Error(`Insufficient stock. Available: ${product.quantity}, Required: ${quantity}`);
    }

    await updateDoc(productRef, {
      quantity: newQuantity,
      updated_at: Timestamp.now()
    });
  },

  restoreProductStock: async (productId: string, quantity: number): Promise<void> => {
    const productRef = doc(db, 'products', productId);
    const productSnap = await getDoc(productRef);

    if (!productSnap.exists()) {
      throw new Error('Product not found');
    }

    const product = convertProduct(productSnap);
    const newQuantity = product.quantity + quantity;

    await updateDoc(productRef, {
      quantity: newQuantity,
      updated_at: Timestamp.now()
    });
  },

  validateStockAvailability: async (items: OrderItem[]): Promise<{ isValid: boolean; errors: string[] }> => {
    const errors: string[] = [];

    for (const item of items) {
      try {
        const productRef = doc(db, 'products', item.product_id);
        const productSnap = await getDoc(productRef);

        if (!productSnap.exists()) {
          errors.push(`Product with ID ${item.product_id} not found`);
          continue;
        }

        const product = convertProduct(productSnap);

        if (product.quantity < item.quantity) {
          errors.push(`Insufficient stock for "${product.name_en}". Available: ${product.quantity}, Required: ${item.quantity}`);
        }
      } catch (error) {
        errors.push(`Error checking product ${item.product_id}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  },

  sendOrderStatusEmail: async (orderId: string, previousStatus: Order['status'], newStatus: Order['status']): Promise<void> => {
    // Only send emails for confirmed and shipped status changes
    if (newStatus !== 'confirmed' && newStatus !== 'shipped') {
      return;
    }

    try {
      // Get the full order details
      const order = await storeService.getOrderById(orderId);
      if (!order) {
        console.error('Order not found for email notification:', orderId);
        return;
      }

      // Prepare email data
      const emailData = {
        orderNumber: order.order_number,
        customerName: order.customer_name,
        customerEmail: order.customer_email,
        status: newStatus,
        totalAmount: order.total_amount,
        items: order.items.map(item => ({
          name: item.product_name,
          quantity: item.quantity,
          price: item.product_price
        })),
        pickupLocation: order.pickup_location,
        pickupDate: order.pickup_date,
        pickupTime: order.pickup_time
      };

      // Send emails to both customer and admin
      const emailPromises = [];

      // Send customer email
      if (newStatus === 'confirmed') {
        emailPromises.push(storeService.sendOrderConfirmationEmail(emailData));
      } else if (newStatus === 'shipped') {
        emailPromises.push(storeService.sendOrderShippedEmail(emailData));
      }

      // Send admin notification email
      emailPromises.push(storeService.sendOrderAdminNotificationEmail(emailData));

      // Wait for all emails to be sent
      await Promise.all(emailPromises);
    } catch (error) {
      console.error('Error sending order status email:', error);
      // Don't throw error to avoid breaking the order update process
      // Email failures should not prevent order status updates
    }
  },

  // Email functions that call Vercel API routes
  sendOrderConfirmationEmail: async (emailData: any): Promise<void> => {
    try {
      const response = await fetch('/api/email/order-confirmation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      await response.json();
    } catch (error) {
      console.error('Error sending order confirmation email via Vercel API:', error);
      throw error;
    }
  },

  sendOrderShippedEmail: async (emailData: any): Promise<void> => {
    try {
      const response = await fetch('/api/email/order-shipped', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      await response.json();
    } catch (error) {
      console.error('Error sending order shipped email via Vercel API:', error);
      throw error;
    }
  },

  sendOrderAdminNotificationEmail: async (emailData: any): Promise<void> => {
    try {
      const response = await fetch('/api/email/order-admin-notification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      await response.json();
    } catch (error) {
      console.error('Error sending order admin notification email via Vercel API:', error);
      throw error;
    }
  },

  sendNewOrderEmail: async (emailData: any): Promise<void> => {
    try {
      const response = await fetch('/api/email/order-new', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      await response.json();
    } catch (error) {
      console.error('Error sending new order email via Vercel API:', error);
      throw error;
    }
  },

  // Utility functions
  generateOrderNumber: (): string => {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substr(2, 5);
    return `CD${timestamp}${random}`.toUpperCase();
  },

  calculateCartTotal: (items: CartItem[]): number => {
    return items.reduce((total, item) => total + (item.product.price * item.quantity), 0);
  },

  // Utility function to get localized product name
  getLocalizedProductName: (product: Product, language: string = 'en'): string => {
    switch (language) {
      case 'pt':
        return product.name_pt;
      case 'fr':
        return product.name_fr;
      case 'en':
      default:
        return product.name_en;
    }
  },

  // Utility function to get localized product description
  getLocalizedProductDescription: (product: Product, language: string = 'en'): string => {
    switch (language) {
      case 'pt':
        return product.description_pt || '';
      case 'fr':
        return product.description_fr || '';
      case 'en':
      default:
        return product.description_en || '';
    }
  },

  // Utility function to get localized category name
  getLocalizedCategoryName: (product: Product, language: string = 'en'): string => {
    switch (language) {
      case 'pt':
        return product.category_pt;
      case 'fr':
        return product.category_fr;
      case 'en':
      default:
        return product.category_en;
    }
  }
};
